import type { IPerson } from "../interfaces";
import type { IPersonCreate } from "../interfaces/models/IPersonCreate";
import Axios from "../utils/axios";

const axios = new Axios();

export type PublicWsgContactStaff = {
  id: string;
  name: string;
  lastName: string;
  title: string;
  email: string;
  photoUpdatedAt?: number;
  isSearchable?: boolean;
  legacyId?: string;
  generatedId?: string;
};

export type ContactTopic = { id: string; text: string };

export type PublicWsgInstitution = {
  addressLines: string[];
  phone: string;
  fax: string;
  email: string;
};

export type PublicWsgContact = {
  staff: PublicWsgContactStaff[];
  topics?: ContactTopic[];
  institution?: PublicWsgInstitution;
};

export const getPublicWsgContact = async (): Promise<PublicWsgContact> => {
  const response = await axios.Get("./person?publicContact=true");
  return response.data;
};

export type WsgContactAdminCard = {
  id: string;
  name: string;
  lastName: string;
  title: string;
  email: string;
  photoUpdatedAt?: number;
  generatedId?: number;
};

export const getWsgContactAdmin = async (): Promise<{
  staff: WsgContactAdminCard[];
  available: WsgContactAdminCard[];
}> => {
  const response = await axios.Get("./person?wsgContactAdmin=true");
  return response.data;
};

export const saveWsgContactAdmin = async (staffIds: string[]) => {
  const response = await axios.Post("./person?wsgContactAdmin=true", { staffIds });
  return response.data;
};

export const getContactTopics = async (): Promise<{ topics: ContactTopic[] }> => {
  const response = await axios.Get("./person?contactTopics=true");
  return response.data;
};

export const saveContactTopic = async (body: {
  action: "create" | "update" | "delete";
  id?: string;
  text?: string;
}): Promise<{ topics: ContactTopic[] }> => {
  const response = await axios.Post("./person?contactTopics=true", body);
  return response.data;
};

export const submitPublicWsgContact = async (message: {
  topic?: string;
  name: string;
  email: string;
  firm: string;
  phone: string;
  comments?: string;
}) => {
  const response = await axios.Post("./person?publicContact=true", message);
  return response.data;
};

export const getPersons = async () => {
  try {
    const response = await axios.Get("/person?withWSG=True");
    return response.data;
  } catch (error) {
    console.error("Error fetching firms:", error);
    return [];
  }
};

export const fetchPersonByFirm = async (firmid: any) => {
  try {
    const params = new URLSearchParams();
    params.append('filter', JSON.stringify({firm: {value: firmid, comparisonType: 'EQUAL'}}))
    const response = await axios.Get(`./person?${params.toString()}`);
    const data = response.data;
    if (data != null) {
      return data;
    }
  } catch (error) {
    console.error("Error fetching data:", error);
    return [];
  }
};

export const fetchPersonByFirmIds = async (firmIds: string[]): Promise<any[]> => {
  if (!firmIds?.length) {
    return [];
  }
  try {
    const filter =
      firmIds.length === 1
        ? { firm: { value: firmIds[0], comparisonType: 'EQUAL' } }
        : { firm: { value: firmIds, comparisonType: 'EQUAL' } };
    const params = new URLSearchParams();
    params.append('filter', JSON.stringify(filter));
    const response = await axios.Get(`./person?${params.toString()}`);
    const data = response.data;
    return data != null ? data : [];
  } catch (error) {
    console.error("Error fetching persons by firm IDs:", error);
    return [];
  }
};

export const searchableProfiles = async (filter = {}) => {
  try {
    const queryString = `/person?serchableProfiles=true
      &filter=${JSON.stringify(filter)}`;
    const response = await axios.Get(queryString);
    return response.data;
  } catch (error) {
    console.error("Error al obtener la información de la columna:", error);
  }
};

export const getPersonById = async (id: string): Promise<IPerson> => {
  try {
    const params = new URLSearchParams();
    params.append('filter', JSON.stringify({id: {value: id, comparisonType: 'EQUAL'}}))
    const response = await axios.Get(`./person?${params.toString()}`);
    return response.data[0];
  } catch (error) {
    console.error("Error fetching data:", error);
    throw error;
  }
};

export const getPersonByUserId = async (id: string): Promise<IPerson> => {
  try {
    const params = new URLSearchParams();
    params.append('filter', JSON.stringify({userId: {value: id, comparisonType: 'EQUAL'}}))
    const response = await axios.Get(`./person?${params.toString()}`);
    return response.data[0];
  } catch (error) {
    console.error("Error fetching data:", error);
    throw error;
  }
}

export const getPersonByLegacyId = async (id: number): Promise<IPerson> => {
  try {
    const params = new URLSearchParams();
    params.append('filter', JSON.stringify({legacyId: {value: id, comparisonType: 'EQUAL'}}))
    const response = await axios.Get(`./person?${params.toString()}`);
    return response.data[0];
  } catch (error) {
    console.error("Error fetching data:", error);
    throw error;
  }
}


const isUuidFormat = (id: string | number): boolean => {
  if (typeof id === "number") {
    return false;
  }
  const uuidRegex =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  return uuidRegex.test(id);
};

export type GetPersonBySegmentOptions = {
  cacheBust?: boolean;
};

async function getPersonFirstByFilter(
  filter: Record<string, unknown>,
  cacheBust?: boolean
): Promise<IPerson | undefined> {
  try {
    const params = new URLSearchParams();
    params.append("filter", JSON.stringify(filter));
    if (cacheBust) {
      params.append("_cb", String(Date.now()));
    }
    const response = await axios.Get(`./person?${params.toString()}`);
    return response.data?.[0] ?? undefined;
  } catch (error) {
    console.error("Error fetching person by filter:", error);
    return undefined;
  }
}

export const getPersonByGeneratedId = async (
  id: number,
  options?: GetPersonBySegmentOptions
): Promise<IPerson | undefined> => {
  return getPersonFirstByFilter(
    { generatedId: { value: id, comparisonType: "EQUAL" } },
    options?.cacheBust
  );
};

export const getPersonByIdOrLegacyId = async (
  id: string | number,
  options?: GetPersonBySegmentOptions
): Promise<IPerson | undefined> => {
  try {
    const idString = String(id);
    const bust = options?.cacheBust;

    if (isUuidFormat(idString)) {
      try {
        const params = new URLSearchParams();
        params.append(
          "filter",
          JSON.stringify({
            id: { value: idString, comparisonType: "EQUAL" },
          })
        );
        if (bust) {
          params.append("_cb", String(Date.now()));
        }
        const response = await axios.Get(`./person?${params.toString()}`);
        return response.data?.[0];
      } catch (error) {
        console.error("Error fetching data:", error);
        return undefined;
      }
    }

    const numericId = typeof id === "number" ? id : parseInt(idString, 10);
    if (isNaN(numericId)) {
      console.warn(`Invalid person ID format: ${id}`);
      return undefined;
    }

    const byLegacyNum = await getPersonFirstByFilter(
      { legacyId: { value: numericId, comparisonType: "EQUAL" } },
      bust
    );
    if (byLegacyNum) {
      return byLegacyNum;
    }

    const byLegacyStr = await getPersonFirstByFilter(
      { legacyId: { value: String(numericId), comparisonType: "EQUAL" } },
      bust
    );
    if (byLegacyStr) {
      return byLegacyStr;
    }

    const byGenerated = await getPersonByGeneratedId(numericId, options);
    if (byGenerated) {
      return byGenerated;
    }

    return undefined;
  } catch (error) {
    console.error(`Error fetching person with ID ${id}:`, error);
    return undefined;
  }
};

export const addPerson = async (person: IPersonCreate): Promise<unknown> => {
  try {
    const response = await axios.Post("person", person);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const updatePerson = async (
  person: any
): Promise<unknown> => {
  try {
    const response = await axios.Put("person", person);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const deletePerson = async (personId: string) => {
  try {
    await axios.Delete('person', personId);
    return true
  } catch (error) {
    throw error;
  }
};

export const restorePerson = async (personId: string) => {
  try {
    await axios.PutDataIntoModel(
      `person?action=restore&id=${encodeURIComponent(personId)}`,
      {},
    );
    return true;
  } catch (error) {
    throw error;
  }
};

export const countPersonsByFirm = async (firmId: string) => {
  try {
    const params = new URLSearchParams();
    params.append('filter', JSON.stringify({firm: {value: firmId, comparisonType: 'EQUAL'}}))
    const response = await axios.Get(`./person?${params.toString()}&count=true`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const countPersonsByOffices = async (officeIds: string | string[]) => {
  try {
    const params = new URLSearchParams();
    params.append('filter', JSON.stringify({mainOffice: {
      value: officeIds, 
      comparisonType: 'EQUAL',
      orFields: ['additionalOffices'],
      orComparisonType: 'CONTAINS'
    }}))
    const response = await axios.Get(`./person?${params.toString()}&count=true`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const countNonAttorneyByOffices = async (
  officeIds: string | string[],
  firmId: string,
) => {
  try {
    const params = new URLSearchParams();
    params.append(
      "filter",
      JSON.stringify({
        mainOffice: {
          value: officeIds,
          comparisonType: "EQUAL",
          orFields: ["additionalOffices"],
          orComparisonType: "CONTAINS",
        },
        firm: { value: firmId, comparisonType: "EQUAL" },
        isAttorney: { value: false, comparisonType: "EQUAL" },
      }),
    );
    const response = await axios.Get(`./person?${params.toString()}&count=true`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const countAttorneyByFirm = async (firmId: string) => {
  try {
    const params = new URLSearchParams();
    params.append('filter', JSON.stringify({
      isAttorney: {value: true, comparisonType: 'EQUAL'},
      firm: {value: firmId, comparisonType: 'EQUAL'},
    }))
    const response = await axios.Get(`./person?${params.toString()}&count=true`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const countAttorneyByMainOffice = async (officeIds: string[], firmId: string) => {
  try {
    const params = new URLSearchParams();
    params.append('filter', JSON.stringify({
      mainOffice: {value: officeIds, comparisonType: 'EQUAL'},
      firm: {value: firmId, comparisonType: 'EQUAL'},
      isAttorney: {value: true, comparisonType: 'EQUAL'}
    }))
    const response = await axios.Get(`./person?${params.toString()}&count=true`);
    return response.data;
  } catch (error) {
    throw error;
  }
}

export const fetchPersonByType = async (type: number) => {
  try {
    const params = new URLSearchParams();
    params.append('filter', JSON.stringify({memberType: {value: type, comparisonType: 'EQUAL'}}))
    const response = await axios.Get(`./person?${params.toString()}`);
    const data = response.data;
    if (data != null) {
      return data;
    }
  } catch (error) {
    console.error("Error fetching data:", error);
    return [];
  }
};


export type PersonAuditScanType =
  | "b2cAllUserIds"
  | "b2cOrphans"
  | "duplicateFirmEmail"
  | "duplicateIdentity";

export interface PersonAuditPagination {
  pageIndex?: number;
  pageSize?: number;
  lastId?: string;
}

export interface PersonAuditScanResult {
  items: Array<{
    id: string;
    generatedId?: number;
    name?: string;
    lastName?: string;
    firm?: string;
    firmName?: string;
    firmEmail?: string;
    userId?: string;
    legacyId?: string;
    role?: string;
    userName?: string;
    duplicateGroup?: string;
    groupCount?: number;
  }>;
  totalCount: number;
  hasMore?: boolean;
  pageIndex?: number;
  scannedInBatch?: number;
  personsWithUserIdTotal?: number;
  nextLastId?: string;
  userIds?: string[];
}

export const runPersonAudit = async (
  audit: PersonAuditScanType,
  pagination?: PersonAuditPagination,
): Promise<PersonAuditScanResult> => {
  const params = new URLSearchParams();
  params.append("audit", audit);
  if (pagination) {
    params.append("pagination", JSON.stringify(pagination));
  }
  const response = await axios.Get(`./person?${params.toString()}`);
  return response.data;
};

export const getPersonLastLoginByFirm = async (firmId: string, role?: string): Promise<any[]> => {
  try {
    const params = new URLSearchParams();
    params.append('lastLogin', 'true');
    params.append('firmId', firmId);
    if (role) {
      params.append('role', role);
    }
    const dataAdmin = await axios.Get(`./person?${params.toString()}`);
    return dataAdmin.data;
  } catch (error) {
    console.error("Error fetching data:", error);
    return [];
  }
};

/** Persons with lastLogin whose `mainOffice` is one of `officeIds` (e.g. directory offices). */
export const getPersonLastLoginByOffices = async (
  firmId: string,
  officeIds: string[],
  role?: string,
): Promise<any[]> => {
  try {
    const params = new URLSearchParams();
    params.append('lastLogin', 'true');
    params.append('firmId', firmId);
    if (role) {
      params.append('role', role);
    }
    const filtered = officeIds.map((id) => String(id ?? '').trim()).filter(Boolean);
    if (filtered.length > 0) {
      params.append('officeIds', JSON.stringify(filtered));
    }
    const dataAdmin = await axios.Get(`./person?${params.toString()}`);
    return dataAdmin.data;
  } catch (error) {
    console.error("Error fetching data:", error);
    return [];
  }
};